import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from '@/hooks/useLocaleStorage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { isAngelIntegratedCountry } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { TheatricalPromotion } from '../../components/TheatricalPromotionContext/types'
import { Flow } from '../../utils'

export const useTheatricalModalController = (
  theatricalPromotion: TheatricalPromotion | null,
  theatricalSlug: string,
  limitedDateModalIsActive: boolean,
): [boolean, () => void] => {
  const { query, asPath, isReady: isRouterReady } = useSafeRouter()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isFreeTicketPromo, isEditReservationFlow, countryCode } = useTicketsContext()
  const { modal, publicDiscount } = theatricalPromotion ?? {}
  const discount = publicDiscount?.activeChildDiscount?.value ?? 0

  const storageKey = useMemo(() => {
    return discount
      ? `discount-modal-opened-${modal?.modalType}-${discount}-${theatricalSlug}`
      : `discount-modal-opened-${modal?.modalType}-${theatricalSlug}`
  }, [discount, modal?.modalType, theatricalSlug])

  const {
    storedValue: hasSeenModal,
    setStoredValue: setHasSeenModal,
    isReady: isLocaleStorageReady,
  } = useLocalStorage<boolean>(storageKey ?? '', false, 'boolean', !modal?.modalType || limitedDateModalIsActive)

  const isAngelIntegrated = isAngelIntegratedCountry(countryCode)
  const shouldHideDiscountsForMarketing = query?.promoOverride === 'true'
  const isSceneModalPlayerPage = asPath?.toLowerCase().includes('scene') && !asPath?.toLowerCase().includes('static')
  const isGuildShareFlow = query.flow === Flow.GUILD_SHARE

  const canShowModal = useMemo(() => {
    return (
      !limitedDateModalIsActive &&
      storageKey &&
      !!modal?.modalType &&
      isRouterReady &&
      isLocaleStorageReady &&
      !hasSeenModal &&
      !isEditReservationFlow &&
      isAngelIntegrated &&
      !shouldHideDiscountsForMarketing &&
      !isSceneModalPlayerPage &&
      !query?.highlightGroupTickets &&
      !query?.groupDiscountCode &&
      !isFreeTicketPromo &&
      !isGuildShareFlow
    )
  }, [
    hasSeenModal,
    isAngelIntegrated,
    isEditReservationFlow,
    isFreeTicketPromo,
    isGuildShareFlow,
    isLocaleStorageReady,
    limitedDateModalIsActive,
    isRouterReady,
    isSceneModalPlayerPage,
    modal?.modalType,
    query?.highlightGroupTickets,
    query?.groupDiscountCode,
    shouldHideDiscountsForMarketing,
    storageKey,
  ])

  const closeModal = useCallback(() => setIsModalOpen(false), [setIsModalOpen])

  useEffect(() => {
    if (canShowModal) {
      setIsModalOpen(true)
      setHasSeenModal(true)
    }
  }, [canShowModal, setHasSeenModal])

  return useMemo(() => {
    return [isModalOpen, closeModal]
  }, [closeModal, isModalOpen])
}
